import { CanceledError, isAxiosError, isCancel } from "axios";
import { revokeSessionCredential, type SessionRequestConfig } from "@/lib/axios";
import { create } from "zustand";
import { APP_CONSTANTS } from "@/config";
import { httpRequest } from "@/lib";
import { AuthState, LoginCredentials } from "@/types";
import { authenticate } from "@/hooks/auth/use-authentication-mutation";

interface AuthStoreState extends AuthState {
  sessionRevision: number;
  sessionStatus: "checking" | "ready" | "unavailable" | "signed-out";
  signIn: (credentials: LoginCredentials) => Promise<void>;
  signOut: () => Promise<void>;
  logoutError: string | null;
  refreshToken: () => Promise<void>;
  getPendingRefresh: () => Promise<void> | undefined;
  /**
   * Re-reads the signed-in user through the same bounded identity check as bootstrap.
   * Used after an OAuth popup closes, when a flag on the user row (today
   * `isSynchronizedWithGoogle`) changed outside the app. It rejects on failure
   * instead of signing out: a blip while refreshing a flag is not a dead
   * session, and the caller shows the failure next to the button.
   */
  refreshUser: () => Promise<void>;
  initialize: () => Promise<void>;
  isDisconnected: boolean;
  isAuthenticating: boolean;
  error: string | null;
  setAuthState: (
    updater:
      | Partial<AuthStoreState>
      | ((state: AuthStoreState) => Partial<AuthStoreState>)
  ) => void;
}

class SessionIdentityMismatch extends Error {}

function validateIdentity(
  nextUser: AuthState["user"], user: AuthState["user"], tenant: string | null,
): asserts nextUser is NonNullable<AuthState["user"]> {
  if (!nextUser || typeof nextUser.id !== "string" || !nextUser.id.trim()
    || (nextUser.tenant_id !== undefined
      && (typeof nextUser.tenant_id !== "string" || !nextUser.tenant_id.trim()))
    || (!tenant && !nextUser.tenant_id)) {
    throw new Error("Invalid refreshed session identity");
  }
  // UserResource omits tenant_id. Laravel authenticates the user in the captured
  // X-Tenant scope; an additive tenant field must still match, when present.
  if ((user && (nextUser.id !== user.id
      || (nextUser.tenant_id && user.tenant_id && nextUser.tenant_id !== user.tenant_id)))
    || (tenant && nextUser.tenant_id && nextUser.tenant_id !== tenant)) {
    throw new SessionIdentityMismatch("Invalid refreshed session identity");
  }
}

export const useAuthStore = create<AuthStoreState>((set, get) => {
  let initialization: { isCurrent: () => boolean; promise: Promise<void> } | undefined;
  let refresh: { isCurrent: () => boolean; promise: Promise<void> } | undefined;

  const endRejectedSession = (error: string | null = "Sessão expirada") => {
    localStorage.removeItem(APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY);
    localStorage.removeItem(APP_CONSTANTS.AUTH_USER_LOCAL_STORAGE_KEY);
    delete httpRequest.defaults.headers.authorization;
    set({ user: null, accessToken: null, isLoading: false, isDisconnected: true,
      sessionRevision: get().sessionRevision + 1, sessionStatus: "signed-out", isAuthenticating: false, error });
  };

  return {
    logoutError: null,
    sessionRevision: 0,
    sessionStatus: "checking",
    user: null,
    accessToken: null,
    isLoading: true,
    isDisconnected: false,
    error: null,
    isAuthenticating: false, // For backwards compatibility with context usage

    setAuthState: (updater) => {
      set((state) => {
        const next = typeof updater === "function" ? updater(state) : updater;
        const identityChanged = next.user !== undefined && (
          next.user?.id !== state.user?.id || next.user?.tenant_id !== state.user?.tenant_id
        );
        return { ...next, sessionRevision: state.sessionRevision + (identityChanged ? 1 : 0) };
      });
    },

    refreshUser: async () => {
      await get().initialize();
      if (get().sessionStatus !== "ready") throw new Error("Session identity unavailable");
    },

    initialize: () => {
      if (get().isAuthenticating || get().isDisconnected) return Promise.resolve();
      if (initialization?.isCurrent()) return initialization.promise;
      const token = localStorage.getItem(APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY);
      const tenant = localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);
      const revision = get().sessionRevision;
      const user = get().user;
      const storeToken = get().accessToken;
      const sameSession = () => get().sessionRevision === revision
        && localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY) === tenant;
      const isCurrent = () => sameSession()
        && localStorage.getItem(APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY) === token
        && get().accessToken === storeToken
        && get().user?.id === user?.id && get().user?.tenant_id === user?.tenant_id;

      if (!token) {
        set({ user: null, accessToken: null, isLoading: false, sessionStatus: "signed-out" });
        return Promise.resolve();
      }
      const attempt = { isCurrent: () => sameSession() && (isCurrent() || Boolean(refresh?.isCurrent())), promise: Promise.resolve() };
      initialization = attempt;
      set({ sessionStatus: "checking", error: null, isLoading: !user });
      attempt.promise = (async () => {
        const config: SessionRequestConfig = {
          _session: { token, tenant, revision }, _skipSessionRecovery: true, timeout: 15_000,
        };
        try {
          const response = await httpRequest.post<AuthState["user"]>("/me", {}, config);
          if (!isCurrent()) return;
          validateIdentity(response.data, user, tenant);
          localStorage.setItem(APP_CONSTANTS.AUTH_USER_LOCAL_STORAGE_KEY, JSON.stringify(response.data));
          httpRequest.defaults.headers.authorization = "Bearer " + token;
          set({ user: response.data, accessToken: token, isLoading: false, isDisconnected: false, sessionStatus: "ready", error: null });
        } catch (error) {
          if (!isCurrent() || isCancel(error)) return;
          if (isAxiosError(error) && error.response?.status === 401) {
            // Explicit identity recovery: refreshToken performs its own bounded /me.
            // The failed POST is never generically replayed, nor is the old token restored.
            try {
              await get().refreshToken();
              if (sameSession()) set({ isLoading: false, isDisconnected: false, sessionStatus: "ready", error: null });
            } catch (recoveryError) {
              if (sameSession() && !isCancel(recoveryError)) {
                set({ isLoading: false, sessionStatus: "unavailable", error: "Não foi possível verificar a sessão. Tente novamente." });
              }
            }
          } else if (error instanceof SessionIdentityMismatch) {
            endRejectedSession();
          } else {
            set({ isLoading: false, sessionStatus: "unavailable", error: "Não foi possível verificar a sessão. Tente novamente." });
          }
        } finally {
          if (initialization === attempt) initialization = undefined;
        }
      })();
      return attempt.promise;
    },

    signIn: async (credentials: LoginCredentials) => {
      const revision = get().sessionRevision + 1;
      const tenant = localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);
      const isCurrent = () => get().sessionRevision === revision
        && localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY) === tenant;
      try {
        set({ sessionRevision: revision, sessionStatus: "checking", isLoading: true, error: null, logoutError: null, isAuthenticating: true });

        const data = await authenticate(credentials);
        if (!isCurrent()) throw new CanceledError("Browser session changed during login");
        const { access_token: token, user } = data;
        if (typeof token !== "string" || !token.trim()) throw new Error("Invalid login response");
        validateIdentity(user, null, tenant);

        localStorage.setItem(
          APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY,
          token
        );
        localStorage.setItem(
          APP_CONSTANTS.AUTH_USER_LOCAL_STORAGE_KEY,
          JSON.stringify(user)
        );

        httpRequest.defaults.headers.authorization = `Bearer ${token}`;
        set({
          user,
          accessToken: token,
          isLoading: false,
          isAuthenticating: false,
          isDisconnected: false,
          sessionStatus: "ready",
        });
      } catch (error) {
        if (!isCurrent()) throw new CanceledError("Browser session changed during login");
        endRejectedSession("Credenciais inválidas");
        throw error;
      }
    },

    signOut: async () => {
      const token = localStorage.getItem(APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY);
      const tenant = localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);
      endRejectedSession(null);
      set({ logoutError: null });
      const revision = get().sessionRevision;
      if (!token) return;
      try {
        await revokeSessionCredential(token, tenant);
      } catch {
        if (get().sessionRevision !== revision) return;
        // Even 401 may be an expired, still-renewable JWT rejected before logout.
        set({ logoutError: "Você saiu desta aba, mas não foi possível confirmar o encerramento da sessão no servidor." });
      }
    },

    getPendingRefresh: () => refresh?.isCurrent() ? refresh.promise : undefined,

    refreshToken: () => {
      if (get().isDisconnected) return Promise.reject(new CanceledError("Browser session ended"));
      if (get().isAuthenticating) return Promise.reject(new Error("Authentication in progress"));
      if (refresh?.isCurrent()) return refresh.promise;

      let token = localStorage.getItem(APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY);
      let storeToken = get().accessToken;
      const tenant = localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);
      const revision = get().sessionRevision;
      const user = get().user;
      const isCurrent = () => get().sessionRevision === revision
        && get().accessToken === storeToken
        && get().user?.id === user?.id
        && get().user?.tenant_id === user?.tenant_id
        && localStorage.getItem(APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY) === token
        && localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY) === tenant;
      const requestConfig = (): SessionRequestConfig => ({
        _session: { token, tenant, revision },
        _skipSessionRecovery: true,
        timeout: 15_000,
      });
      const attempt = { isCurrent, promise: Promise.resolve() };
      refresh = attempt;
      set({ sessionStatus: "checking", error: null });
      attempt.promise = (async () => {
        try {
          if (!token) throw new Error("No active browser session");
          const response = await httpRequest.post<unknown>("/refresh", { token }, requestConfig());
          if (!isCurrent()) throw new CanceledError("Browser session changed during refresh");
          const data = response.data;
          if (typeof data !== "object" || data === null || !("access_token" in data)
            || typeof data.access_token !== "string" || !data.access_token.trim()) {
            throw new Error("Invalid refresh response");
          }

          token = data.access_token;
          storeToken = token;
          localStorage.setItem(APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY, token);
          httpRequest.defaults.headers.authorization = "Bearer " + token;
          set({ accessToken: token });

          // Keep identity/drafts mounted while validating the rotated credential.
          // This read cannot join the refresh that is waiting for its result.
          const identity = await httpRequest.post<AuthState["user"]>("/me", {}, requestConfig());
          if (!isCurrent()) throw new CanceledError("Browser session changed during refresh");
          const nextUser = identity.data;
          validateIdentity(nextUser, user, tenant);
          localStorage.setItem(APP_CONSTANTS.AUTH_USER_LOCAL_STORAGE_KEY, JSON.stringify(nextUser));
          set({ user: nextUser, sessionStatus: "ready", error: null });
        } catch (error) {
          if (!isCurrent()) throw new CanceledError("Browser session changed during refresh");
          if ((isAxiosError(error) && error.response?.status === 401) || error instanceof SessionIdentityMismatch) {
            endRejectedSession();
          } else if (!isCancel(error)) {
            set({ sessionStatus: "unavailable", error: "Não foi possível verificar a sessão. Tente novamente." });
          }
          throw error;
        } finally {
          if (refresh === attempt) refresh = undefined;
        }
      })();
      return attempt.promise;
    },
  };
});
